import axios from "axios";
import { getDeviceInfo } from "../../../helpers/getDeviceInfo";
import { config } from "../../../../config/env";
import traceId from "../../../../redux/traceId";
import { fetchFlagEnabled } from "../../../adapter/UnleashFeatureFlagAdapter";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlag";

const ME_FLAG_FETCH_TIMEOUT_MS = 3000;

export const LOGIN_URL = `${config.jusfyBackend.apiUrl}/auth/login`;
export const LOGIN_IDENTIFY_URL = `${config.jusfyBackend.apiUrl}/auth/login/identify`;
export const REGISTER_URL = `${config.jusfyBackend.apiUrl}/auth/register`;
export const REQUEST_PASSWORD_URL = `${config.jusfyBackend.apiUrl}/auth/forgot-password`;
export const LOGOUT_URL = `${config.jusfyBackend.apiUrl}/auth/logout`;
export const VERIFY_2FA_URL = `${config.jusfyBackend.apiUrl}/auth/2fa/verify`;
export const RESEND_2FA_URL = `${config.jusfyBackend.apiUrl}/auth/2fa/resend-code`;

export const REDIRECT_TO_COMMUNITY = `${config.jusfyBackend.apiUrl}/auth/redirect`;
export const REGISTER_EXTERNAL_URL = `${config.jusfyBackend.apiUrl}/auth/register/external`;

export const ME_URL = `${config.jusfyBackend.apiUrl}/me`;
export const ME_V2_URL = `${config.jusfyBackend.apiUrl}/v2/me`;

export async function login(email, password, recaptchaToken, recaptchaVersion) {
  const deviceInfo = getDeviceInfo();

  const payload = {
    email,
    password,
    recaptchaToken,
    recaptchaVersion,
    ...deviceInfo,
  };

  const headers = {
    "x-device-id": deviceInfo.deviceFingerprint,
    "x-request-id": deviceInfo.requestId,
  };

  delete payload.deviceFingerprint;

  const response = await axios.post(LOGIN_URL, payload, { headers });

  return response;
}

export function identifyLogin(email, recaptchaToken, recaptchaVersion) {
  return axios.post(LOGIN_IDENTIFY_URL, {
    email,
    recaptchaToken,
    recaptchaVersion,
  });
}

export function register(email, fullname, username, password) {
  return axios.post(REGISTER_URL, { email, fullname, username, password });
}

export function requestPassword(email) {
  return axios.post(REQUEST_PASSWORD_URL, { email });
}

export function logout(loginId) {
  return axios.post(LOGOUT_URL, { loginId });
}

export function verifyTwoFactor(code, token) {
  const deviceInfo = getDeviceInfo();
  return axios.post(
    VERIFY_2FA_URL,
    { code },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-trace-id": traceId,
        "x-device-id": deviceInfo.deviceFingerprint,
      },
    }
  );
}

export async function getUserByToken() {
  // Authorization head should be fulfilled in interceptor.
  const useV2 =
    (await fetchFlagEnabled(FEATURE_FLAGS.KILL_SWITCH.DISABLE_CONSUME_DEPRECATED_ME_ENDPOINT, {
      timeout: ME_FLAG_FETCH_TIMEOUT_MS,
    })) === true;

  return axios.get(useV2 ? ME_V2_URL : ME_URL);
}

export function redirectToCommunity(queryParams) {
  return axios.get(`${REDIRECT_TO_COMMUNITY}${queryParams}`);
}

export function registerExternal(name, email, password, confirmation_password) {
  return axios.post(REGISTER_EXTERNAL_URL, {
    name,
    email,
    password,
    confirmation_password,
  });
}

export const AUTH_SERVICE_REFRESH_PATH = "/api/v1/oauth/token/refresh";
export const AUTH_SERVICE_LOGOUT_PATH = "/api/v1/oauth/logout";

export function authServiceEndpoint(path) {
  const base = config.authService?.url;
  if (!base) {
    throw new Error("AUTH_SERVICE_URL_MISSING");
  }
  return `${String(base).replace(/\/$/, "")}${path}`;
}

function postAuthService(path, requestConfig = {}) {
  const client = axios.create({ withCredentials: true });
  return client.request({
    method: "POST",
    url: authServiceEndpoint(path),
    withCredentials: true,
    skipAuthRefresh: true,
    ...requestConfig,
  });
}

export async function refreshAuthServiceSession() {
  const { data } = await postAuthService(AUTH_SERVICE_REFRESH_PATH, {
    headers: { "X-Requested-With": "XMLHttpRequest" },
    data: { clientId: config.hydra.clientId },
  });
  return data;
}

export function logoutAuthService() {
  return postAuthService(AUTH_SERVICE_LOGOUT_PATH);
}

export function resendTwoFactorCode(token) {
  return axios.post(
    RESEND_2FA_URL,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}
